import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  CheckRounded,
  EventOutlined,
  GroupsOutlined,
  ListAltOutlined,
  PersonRemoveOutlined,
  RemoveRounded,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType } from "services/constants/designTokens";
import { esiReasonCodeHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { ChipMultiSelect, RecordHeaderCard, StatCardGrid, StatutoryList } from "pages/PayRoll/Shared/statutoryUi";
import { fetchReasonCode, nameOf, saveReasonMapping } from "./esiConfigApi";

// Reason Codes tab (C5) and the Edit Reason Mapping drawer (C5a), on the
// ESI API. The ESIC reason-code list is Cauver metadata and read-only; the
// company maps its own exit reasons, leave types and situations to it once
// (one reason -> one code). Code 4 (out of coverage) is set by the system.

const ALL = "all";
const STATUS_COLORS = { Mapped: "#12B76A", "Not used": "#6B7280" };
const { orange, pink, indigo } = detailAccentPalette;

// used_recently is null until the Reports APIs exist.
const recentText = (recent) => {
  if (!recent) return { label: "ESIC files", value: "Not available yet" };
  if (typeof recent === "string") return { label: "ESIC files", value: recent };
  const employees = (recent.employees || []).map(nameOf).filter(Boolean);
  return {
    label: nameOf(recent.file) || recent.label || "ESIC files",
    value: employees.length ? `${employees.length} employee${employees.length === 1 ? "" : "s"} · ${employees.join(", ")}` : recent.text || "Not used recently",
  };
};

const MappingPanel = ({ open, row, domain, companyId, onClose, onSaved }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [values, setValues] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const code = row?.code;
  useEffect(() => {
    if (!open || code == null) return undefined;
    setDetail(null);
    setError("");
    setSaveError("");
    const request = new AbortController();
    fetchReasonCode({ domain, companyId, code, signal: request.signal })
      .then((data) => {
        setDetail(data);
        setValues(data.chips.map((item) => item.value));
      })
      .catch((err) => {
        if (err.name !== "AbortError") setError(err.message || "Couldn't load this code.");
      });
    return () => request.abort();
  }, [open, code, domain, companyId]);

  if (!row) return null;
  const canMap = detail ? detail.canMap : false;
  const status = values.length ? "Mapped" : "Not used";
  const recent = recentText(detail?.usedRecently);

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      const result = await saveReasonMapping({ domain, companyId, code, values });
      showToast("success", result.message || `Reason mapping for ESIC code ${code} saved.`);
      onSaved(result.data);
    } catch (err) {
      setSaveError(err.message);
      showToast("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Edit Reason Mapping"
      subtitle={`ESIC code ${row.code} · ${row.label}`}
      bodySx={panelBodySx}
      footer={
        canMap ? (
          <PanelActionFooter onCancel={onClose} primaryLabel="Save" primaryProps={{ onClick: save, loading: saving, disabled: !detail }} />
        ) : (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <PrimaryButton onClick={onClose}>Close</PrimaryButton>
          </Mui.Stack>
        )
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={String(row.code)}
          title={row.label}
          chip={<RequestStatusChip status={status} colorCode={STATUS_COLORS[status]} />}
          meta="ESIC code list item · name and number cannot be edited"
          tone={values.length ? "teal" : "slate"}
        />
        <RequestDetailsSection
          title="CODE RULES (SET BY ESIC, READ-ONLY)"
          fields={[
            {
              icon: EventOutlined,
              label: "Last working day",
              value: row.lwd ? "Required in the file" : "Not needed",
              helper: row.lwd ? "Taken from the employee's exit date" : undefined,
              accent: orange,
            },
            {
              icon: PersonRemoveOutlined,
              label: "Effect after upload",
              value: row.removes ? "IP is removed from the employer's portal record" : "IP stays on the employer's portal record",
              helper: row.removes ? "Will not appear in later months' files" : "Appears again when days are paid",
              accent: pink,
              multiline: true,
            },
          ]}
        />
        {error ? (
          <RequestInfoBanner tone="danger" title="Couldn't load this code." body={error} />
        ) : !detail ? (
          <Mui.Box role="status" aria-label="Loading reason code">
            <Loader info="Loading..." minHeight="20vh" />
          </Mui.Box>
        ) : (
          <>
            {!detail.canMap ? (
              <RequestInfoBanner tone="warning" body="This code is applied automatically by the system and can't be mapped." />
            ) : null}
            <FormSection
              title="Mapped Cauver Reasons"
              info="Exit reasons (People › Exit reasons), leave types and situations. Each one can map to only one ESIC code."
            >
              <Field wide label="Exit reasons, leave types and situations" error={saveError || undefined}>
                <ChipMultiSelect value={values} onChange={setValues} options={detail.options} placeholder="+ Add" disabled={!detail.canMap} error={saveError || undefined} />
              </Field>
            </FormSection>
            <RequestDetailsSection title="USED RECENTLY" fields={[{ icon: GroupsOutlined, label: recent.label, value: recent.value, accent: indigo, multiline: true }]} />
          </>
        )}
        <RequestInfoBanner body="Changes apply to files generated from now on. Files already generated keep the codes they were built with." />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const EsiReasonCodesTab = ({ domain, companyId, data, onChange }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [editCode, setEditCode] = useState(null);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "esi_reason_codes_saved_views",
    fullHeaders: esiReasonCodeHeader,
  });

  const allRows = data.codes;
  const rows = card === ALL ? allRows : allRows.filter((row) => row.status === card);

  const cards = [
    { key: ALL, title: "ESIC reason codes", value: data.cards.esic_codes ?? allRows.length, icon: ListAltOutlined, tone: "blue" },
    { key: "Mapped", title: "Mapped", value: data.cards.mapped ?? allRows.filter((row) => row.status === "Mapped").length, icon: CheckRounded, tone: "green" },
    { key: "Not used", title: "Not used", value: data.cards.not_used ?? allRows.filter((row) => row.status === "Not used").length, icon: RemoveRounded, tone: "amber" },
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case "code":
        return <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: 700 }}>{row.code}</Mui.Typography>;
      case "reasons_label":
        return row.chips.length ? (
          <Mui.Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {row.chips.map((item) => (
              <ToneChip key={item.value} label={item.label} tone="blue" />
            ))}
          </Mui.Stack>
        ) : (
          <Mui.Typography sx={{ ...designType.tableBodyCell, color: designColors.slate500 }}>Not used</Mui.Typography>
        );
      case "status":
        return <RequestStatusChip status={row.status} colorCode={STATUS_COLORS[row.status]} />;
      default:
        return row[key] ?? "—";
    }
  };

  return (
    <>
      <StatCardGrid cards={cards} value={card} onChange={setCard} />
      {data.verifiedNote ? <Mui.Typography sx={{ ...designType.captionText, mb: 1 }}>{data.verifiedNote}</Mui.Typography> : null}
      <StatutoryList
        title="ESI Reason Codes"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setEditCode(row)}
        selectedKey={editCode?.code}
        itemLabel="codes"
        searchPlaceholder="Search code or reason"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />

      <MappingPanel
        open={Boolean(editCode)}
        row={editCode}
        domain={domain}
        companyId={companyId}
        onClose={() => setEditCode(null)}
        onSaved={(next) => {
          onChange(next);
          setEditCode(null);
        }}
      />
    </>
  );
};
